import { useMemo, useState, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '@/app/store/store';
import { posCartBuild } from '../../config/posCart.build';
import { PosCartItem } from '../../domain/models/PosCartItem';
import { FetchPosCartItemsArgs } from '../../domain/interfaces/PosCartRepository';

export function usePosCart() {
  const dispatch = useDispatch<AppDispatch>();
  const { service } = useMemo(() => posCartBuild(dispatch), [dispatch]);
  const [items, setItems] = useState<PosCartItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingNextPage, setIsLoadingNextPage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const lastArgsRef = useRef<FetchPosCartItemsArgs | null>(null);

  const fetchItems = useCallback(async (args: FetchPosCartItemsArgs) => {
    setIsLoading(true);
    setError(null);
    setPage(1);
    setHasMore(true);

    const queryArgs: FetchPosCartItemsArgs = {
      ...args,
      page: args.page ?? 1,
      size: args.size ?? 10,
    };

    lastArgsRef.current = queryArgs;

    try {
      const res = await service.getPosCartItems(queryArgs);
      
      if (!Array.isArray(res)) {
        setItems([]);
        setHasMore(false);
        return [];
      }

      setItems(res);
      if (res.length < 10) {
        setHasMore(false);
      }
      return res;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch store cart items';
      setError(msg);
      setHasMore(false);
      throw e;
    } finally {
      setIsLoading(false);
    }
  }, [service]);

  const fetchNextPage = useCallback(async () => {
    if (isLoading || isLoadingNextPage || !hasMore || !lastArgsRef.current) {
      return;
    }

    setIsLoadingNextPage(true);
    const nextPage = page + 1;

    const queryArgs: FetchPosCartItemsArgs = {
      ...lastArgsRef.current,
      page: nextPage,
      size: 10,
    };

    try {
      const res = await service.getPosCartItems(queryArgs);

      if (!Array.isArray(res) || res.length === 0) {
        setHasMore(false);
        return;
      }

      setItems((prev) => {
        const existingIds = new Set(prev.map((item) => item.designId));
        const filteredNewItems = res.filter((item) => !existingIds.has(item.designId));
        return [...prev, ...filteredNewItems];
      });

      setPage(nextPage);

      if (res.length < 10) {
        setHasMore(false);
      }
    } catch {
      setHasMore(false);
    } finally {
      setIsLoadingNextPage(false);
    }
  }, [service, page, isLoading, isLoadingNextPage, hasMore]);

  return {
    items,
    isLoading,
    isLoadingNextPage,
    error,
    hasMore,
    page,
    fetchItems,
    fetchNextPage,
  };
}

